import { UserLanguage } from '../../../../features/account/types/User'
import { LanguageMaps } from '../../../api/language'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { InternalMarket, MarketMutation, SavedMarket } from '../types/InternalMarket'

export const marketLanguages: UserLanguage[] = ['en', 'ja', 'zh']

export type MarketRow = {
  market: InternalMarket
  names: ContentLanguageTranslation
}

export type MarketFormValues = {
  names: ContentLanguageTranslation
  iso: string
}

const compareNames = (a: MarketRow, b: MarketRow) => {
  const nameA = a.market.name.toLowerCase()
  const nameB = b.market.name.toLowerCase()
  return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
}

// The `markets` language group is keyed by ISO code.
export const readMarketNames = (languageMaps: LanguageMaps | undefined, iso: string): ContentLanguageTranslation =>
  Object.fromEntries(marketLanguages.map((language) => [language, languageMaps?.[language]?.markets?.[iso]?.name ?? '']))

// `GET markets` comes back in no fixed order, so rows are sorted by name.
export const buildMarketRows = (markets: InternalMarket[], languageMaps: LanguageMaps | undefined): MarketRow[] =>
  markets.map((market) => ({ market, names: readMarketNames(languageMaps, market.iso) })).sort(compareNames)

export const toMarketFormValues = (row?: MarketRow): MarketFormValues => ({
  names: row?.names ?? {},
  iso: row?.market.iso ?? '',
})

export const toMarketMutation = (row: MarketRow | undefined, values: MarketFormValues): MarketMutation => {
  const names = Object.fromEntries(marketLanguages.map((language) => [language, values.names[language] ?? '']))
  const edited = { name: names.en ?? '', names, iso: values.iso.trim() }
  return row ? { ...row.market, ...edited } : edited
}

export type MarketIsoError = 'required' | 'invalid' | 'taken'

// The code keys the language map, so it can't contain dots or start with `$`.
export const getMarketIsoError = (iso: string, markets: InternalMarket[], editedId?: string): MarketIsoError | undefined => {
  const code = iso.trim()
  if (!code) return 'required'
  if (code !== iso || code.includes('.') || code.startsWith('$')) return 'invalid'
  if (markets.some((market) => market.id !== editedId && market.iso.toLowerCase() === code.toLowerCase())) return 'taken'
  return undefined
}

export type MarketField = 'name' | 'iso' | 'names'

export const marketFieldLabelKeys: Record<MarketField, string> = {
  name: 'internal-service-management:markets_name',
  iso: 'internal-service-management:markets_iso',
  names: 'internal-service-management:markets_translations',
}

export const getUnstoredMarketFields = (sent: MarketMutation, saved: SavedMarket): MarketField[] => {
  const stored = saved.market
  if (!stored) return ['name', 'iso', 'names']
  const unstored: MarketField[] = []
  if (stored.name !== sent.name) unstored.push('name')
  if (stored.iso !== sent.iso) unstored.push('iso')
  if (marketLanguages.some((language) => (saved.names[language] ?? '') !== (sent.names[language] ?? ''))) unstored.push('names')
  return unstored
}
